import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container mx-auto max-w-screen-2xl" }
const _hoisted_2 = { class: "mb-6 w-full space-y-3" }
const _hoisted_3 = {
  key: 0,
  class: "flex w-full space-x-2"
}
const _hoisted_4 = {
  key: 0,
  class: "pb-3"
}
const _hoisted_5 = {
  key: 0,
  class: "rounded-3xl bg-sky-blue-200 p-6"
}
const _hoisted_6 = { class: "mt-10 w-full text-center" }
const _hoisted_7 = {
  key: 1,
  class: "rounded-3xl bg-sky-blue-200 p-6"
}

import { ref, onMounted, computed } from "vue";
import { storeToRefs } from "pinia";

import { getTranslation } from "~/js/composables/translations";
import { useSetQueryArrayUrlify, useSetQueryParams } from "~/js/composables/query-parameters";

import { Translations } from "~/js/interfaces/translations";
import { Category } from "~/js/interfaces/knowledge-hub";
import { Consent, ConsentFilter } from "~/js/interfaces/consent-types";

import { useFileManagerStore } from "~/js/store/file-manager";
import { hasConsent } from "~/js/utils/cookie-script";

import ButtonLoadMore from "~/vue/atoms/buttons/ButtonLoadMore.vue";
import ButtonPrimary from "~/vue/atoms/buttons/ButtonPrimary.vue";
import TextH4 from "~/vue/atoms/texts/TextH4.vue";
import Grid from "~/vue/organisms/grids/GridDocuments.vue";
import GridConsent from "~/vue/organisms/grids/GridFileManagerConsent.vue";
import GridSkeleton from "~/vue/organisms/grids/GridSkeleton.vue";
import FilterFileManager from "~/vue/organisms/filters/FilterFileManager.vue";
import HeaderGrids from "~/vue/molecules/headers/HeaderGrids.vue";

interface Props {
	consents?: Array<Consent> | null;
	corporate: Array<Category>;
	heading: string;
	meeting: Array<Category>;
	mode?: "dark" | "light";
	month: Array<Category>;
	owner: string;
	report: Array<Category>;
	translations?: Translations | null;
	utilities?: "";
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ViewFileManager',
  props: {
    consents: { default: [] },
    corporate: {},
    heading: {},
    meeting: {},
    mode: { default: "dark" },
    month: {},
    owner: {},
    report: {},
    translations: { default: null },
    utilities: { default: "" }
  },
  setup(__props: any) {

const props = __props;

const fileManagerStore = useFileManagerStore();
const { documents, pagination, facets, loading, filters } = storeToRefs(fileManagerStore);

const grid = ref(null);
const consentGiven = ref(props.consents ? false : true);
const activeConsent = ref(null);
const activeConsentId = ref(null);

const onLoadMore = () => {
	fileManagerStore.fetch(props.owner, false);
};

const documentsByType = computed(() => {
	return documents.value
		.map((doc) => doc.document)
		.reduce(function (rv, x) {
			(rv[x["fileType"]] = rv[x["fileType"]] || []).push(x);
			return rv;
		}, {});
});

const excludeFiltersForConsent = computed(() => {
	let exclude: Array<string> = [];

	if (activeConsent.value) {
		exclude.push(activeConsent.value.filters.map((f) => f.type));
	}

	exclude = exclude.flat();

	return exclude.filter((filter: string, index: number) => exclude.indexOf(filter) === index);
});

const onClearConsent = () => {
	// Consent filters must be removed from the URL so a reload cannot restore
	// a filter that the visitor has explicitly left.
	activeConsent.value?.filters.forEach((filter) => {
		useSetQueryArrayUrlify(filter.type, []);
	});

	fileManagerStore.resetFilters();
	activeConsent.value = null;
	activeConsentId.value = null;
	consentGiven.value = false;
};

const onConsentGiven = (filter: ConsentFilter) => {
	// Build the consent filter before searching. Calling resetFilters() and
	// setFilter() with their default behavior creates competing requests, which
	// can allow the unfiltered response to replace the filtered result.
	fileManagerStore.resetFilters(false);

	activeConsent.value = filter;

	const valuesByType = filter.filters.reduce<Record<string, Array<string>>>((values, f) => {
		values[f.type] = [...(values[f.type] || []), f.slug];
		fileManagerStore.setFilter(f.type, f.slug, true, false);
		return values;
	}, {});

	Object.entries(valuesByType).forEach(([type, values]) => {
		useSetQueryArrayUrlify(type, values);
	});

	consentGiven.value = true;
	fileManagerStore.fetch(props.owner, true);
};

onMounted(() => {
	const params = useSetQueryParams();

	setTimeout(() => {
		if (props.consents && props.consents.length > 0 && params.length > 0) {
			let consentForQuery: Consent | null = null;

			props.consents.forEach((consent) => {
				const match = consent.filter.filters.filter((filter) =>
					params.some(
						(param) =>
							param.type === filter.type && param.values.some((value) => value.name === filter.slug),
					),
				);

				if (match.length === consent.filter.filters.length) {
					if (hasConsent(consent.categories)) {
						consentGiven.value = true;
						activeConsent.value = consent.filter;
					} else {
						consentGiven.value = false;
						activeConsent.value = consent.filter;
						activeConsentId.value = consent.id;
					}
				}
			});
		}

		fileManagerStore.fetch(props.owner, true, params);
	}, 20);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(HeaderGrids, {
        heading: __props.heading,
        results: !_unref(loading) && consentGiven.value ? _unref(pagination).foundResults : null,
        "show-loader": _unref(loading) || consentGiven.value
      }, null, 8, ["heading", "results", "show-loader"]),
      (consentGiven.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (consentGiven.value && __props.consents)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(ButtonPrimary, {
                    size: "xsmall",
                    type: "button",
                    icon: "chevron-left",
                    content: "Back",
                    download: "",
                    "icon-position": "before",
                    onClick: onClearConsent
                  })
                ]))
              : _createCommentVNode("", true),
            _createVNode(FilterFileManager, {
              translations: __props.translations,
              meeting: __props.meeting,
              report: __props.report,
              corporate: __props.corporate,
              month: __props.month,
              exclude: excludeFiltersForConsent.value
            }, null, 8, ["translations", "meeting", "report", "corporate", "month", "exclude"])
          ]))
        : _createCommentVNode("", true)
    ]),
    (consentGiven.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_unref(loading))
            ? (_openBlock(), _createBlock(GridSkeleton, {
                key: 0,
                size: _unref(pagination).itemsPerPage,
                mode: __props.mode
              }, null, 8, ["size", "mode"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "grid",
            ref: grid
          }, [
            (_unref(documents) && _unref(pagination).itemsPerPage !== 250)
              ? (_openBlock(), _createBlock(Grid, {
                  key: 0,
                  documents: _unref(documents).map((item) => item.document),
                  mode: __props.mode,
                  translations: __props.translations
                }, null, 8, ["documents", "mode", "translations"]))
              : _createCommentVNode("", true),
            (_unref(documents) && _unref(pagination).itemsPerPage === 250)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(documentsByType.value, (groupedDocuments, i) => {
                  return (_openBlock(), _createElementBlock("section", {
                    key: i,
                    class: "mb-10"
                  }, [
                    _createVNode(TextH4, { utilities: "mb-4 block w-full" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(getTranslation)(__props.translations, i)), 1)
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(Grid, {
                      documents: groupedDocuments,
                      mode: __props.mode,
                      translations: __props.translations
                    }, null, 8, ["documents", "mode", "translations"])
                  ]))
                }), 128))
              : _createCommentVNode("", true)
          ], 512),
          _createElementVNode("div", _hoisted_6, [
            (_unref(pagination).current < _unref(pagination).totalPages)
              ? (_openBlock(), _createBlock(ButtonLoadMore, {
                  key: 0,
                  title: _unref(getTranslation)(__props.translations, 'Load more documents'),
                  loading: _unref(loading),
                  ga: {
						category: 'file-manager-load-more',
						action: 'click',
						label: _unref(pagination).current,
					},
                  utilities: "mx-auto",
                  onClick: onLoadMore
                }, null, 8, ["title", "loading", "ga"]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (!consentGiven.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createVNode(GridConsent, {
            consents: __props.consents,
            "active-consent-id": activeConsentId.value,
            onConsentGiven: _cache[0] || (_cache[0] = (filter) => onConsentGiven(filter)),
            onConsentDeclined: _cache[1] || (_cache[1] = () => _unref(fileManagerStore).resetFilters())
          }, null, 8, ["consents", "active-consent-id"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})